/**
 * Section for entering physical stock counts
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Chip,
    Alert
} from "@mui/material";
import { DmsAltBox, DmsButton, intl } from "dmsbasereact";
import CalculateIcon from "@mui/icons-material/Calculate";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

/**
 * @param {Array} stockItems - Current stock items from system
 * @param {Function} onCompare - Callback when user wants to compare counts
 */
export const PhysicalCountSection = ({ stockItems = [], onCompare = () => {} }) => {
    const [physicalCounts, setPhysicalCounts] = useState({});
    const [showDifferences, setShowDifferences] = useState(false);

    const handleCountChange = (batchPk, value) => {
        const numValue = parseInt(value, 10);
        const clamped = isNaN(numValue) ? "" : Math.max(0, numValue);
        setPhysicalCounts((prev) => ({
            ...prev,
            [batchPk]: clamped
        }));
    };

    const getDifference = (item) => {
        const physical = physicalCounts[item.batch_pk];
        if (physical === undefined || physical === "") return null;
        return physical - (item.quantity || 0);
    };

    const itemsWithDifferences = stockItems.filter((item) => {
        const diff = getDifference(item);
        return diff !== null && diff !== 0;
    });

    const handleCompare = () => {
        setShowDifferences(true);
        if (onCompare) {
            const differences = itemsWithDifferences.map((item) => ({
                batch_pk: item.batch_pk,
                batch_number: item.batch_number,
                part_name: item.part_name,
                system_quantity: item.quantity || 0,
                physical_quantity: physicalCounts[item.batch_pk],
                difference: getDifference(item)
            }));
            onCompare(differences);
        }
    };

    return (
        <DmsAltBox.Card>
            <DmsAltBox.Header>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={1}>
                        <CalculateIcon color="primary" />
                        <Typography variant="h6">
                            {intl.get("PhysicalCount").d("Physical Count")}
                        </Typography>
                    </Box>
                    <DmsButton
                        icon={<CompareArrowsIcon />}
                        text={intl.get("CompareWithSystem").d("Compare with System")}
                        variant="primary"
                        size="small"
                        onClick={handleCompare}
                        disabled={Object.keys(physicalCounts).length === 0}
                    />
                </Box>
            </DmsAltBox.Header>
            <DmsAltBox.Body>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {intl.get("PhysicalCountInstructions").d(
                        "Enter the physical count for each item. Items with differences will be highlighted."
                    )}
                </Typography>

                {showDifferences && itemsWithDifferences.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {intl.get("FoundDifferences", { count: itemsWithDifferences.length })
                            .d("Found {count} item(s) with differences")}
                    </Alert>
                )}

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{intl.get("Batch").d("Batch")}</TableCell>
                                <TableCell>{intl.get("Product").d("Product")}</TableCell>
                                <TableCell align="right">
                                    {intl.get("SystemQuantity").d("System Qty")}
                                </TableCell>
                                <TableCell align="center">
                                    {intl.get("PhysicalQuantity").d("Physical Qty")}
                                </TableCell>
                                <TableCell align="center">
                                    {intl.get("Difference").d("Difference")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stockItems.map((item) => {
                                const diff = getDifference(item);
                                const hasDiff = diff !== null && diff !== 0;

                                return (
                                    <TableRow
                                        key={item.batch_pk}
                                        sx={{
                                            bgcolor: hasDiff
                                                ? diff > 0
                                                    ? "success.lighter"
                                                    : "error.lighter"
                                                : "inherit"
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {item.batch_number || "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{item.part_name || "-"}</TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold">
                                                {item.quantity || 0}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={physicalCounts[item.batch_pk] ?? ""}
                                                onChange={(e) =>
                                                    handleCountChange(item.batch_pk, e.target.value)
                                                }
                                                inputProps={{ min: 0, style: { textAlign: "center" } }}
                                                sx={{ width: 100 }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {diff !== null ? (
                                                <Chip
                                                    label={diff > 0 ? `+${diff}` : diff}
                                                    color={
                                                        diff === 0
                                                            ? "default"
                                                            : diff > 0
                                                            ? "success"
                                                            : "error"
                                                    }
                                                    size="small"
                                                    variant={diff === 0 ? "outlined" : "filled"}
                                                />
                                            ) : (
                                                "-"
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {stockItems.length === 0 && (
                    <Typography color="textSecondary" textAlign="center" py={4}>
                        {intl.get("NoStockItemsToCount").d("No stock items available for counting")}
                    </Typography>
                )}
            </DmsAltBox.Body>
        </DmsAltBox.Card>
    );
};

PhysicalCountSection.propTypes = {
    stockItems: PropTypes.arrayOf(
        PropTypes.shape({
            batch_pk: PropTypes.number.isRequired,
            batch_number: PropTypes.string,
            part_name: PropTypes.string,
            quantity: PropTypes.number
        })
    ),
    onCompare: PropTypes.func
};

